import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import { InfoBanner } from "@services/ui-components";
import { getBusinessTypeOptions } from "../BusinessTypeSelector/BusinessTypeOptions";
import { BUSINESS_TYPE, BusinessType } from "../BusinessTypeSelector/businessType";
import CreateServiceBusinessBranchForm from "./tabs/CreateServiceBusinessBranchForm/CreateServiceBusinessBranchForm";
import CreateFoodBusinessBranchForm from "./tabs/CreateFoodBusinessBranchForm/CreateFoodBusinessBranchForm";
import CreateOtherBusinessBranchForm from "./tabs/CreateOtherBusinessBranchForm/CreateOtherBusinessBranchForm";
import MenuLanguageVersionSelectContainer from "../Me-menu/components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useReactiveVar } from "@apollo/client";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import { AllModulesTagName } from "../../utils/enums";
import { useGetLanguagesForCreateBranchQuery } from "@me-team/host/main/createBranch/graphql/createBranch.hooks";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { CompanyAccess } from "@me-team/host/main/graphql/types";

//  TODO требуется реффакторинг: нужно вынести из форм выполнение запросов query в родительский
//   компонент CreateBranch, чтоб их не дублировать и не вызывать повторно при переключении
//   между типами бизнеса в каждой форме, а вызывать прирендере один раз при рендере всей
//   страницы, закэшировать и пропсами уже прокидывать готовые данные по формам (желательно
//   сразу делать и в создании компании, там аналогичная ситация)

const CreateBranch = () => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [selectedBusinessType, setSelectedBusinessType] = useState<BusinessType>(BUSINESS_TYPE.SERVICE);
    const businessTypeOptions = useMemo(() => getBusinessTypeOptions(t), [t]);
    const enabledMenuModule: boolean = useModulesAccess([AllModulesTagName.MENU]);
    const canCreateBranch = useRoleAccess(CompanyAccess.BranchCreate);
    const { data: languageVersionsData, loading: languageVersionsLoading } = useGetLanguagesForCreateBranchQuery({
        variables: { companyId: Number(currentCompanyId) },
        skip: !currentCompanyId,
        context: { errorType: "local" },
    });
    const isPrimaryLanguage = !!languageVersionsData?.user?.company?.primaryLanguage;

    const activeBusinessTypeForm = useMemo(() => {
        switch (selectedBusinessType) {
            case BUSINESS_TYPE.SERVICE:
                return (
                    <CreateServiceBusinessBranchForm
                        selectedBusinessType={selectedBusinessType}
                        businessTypeOptions={businessTypeOptions}
                        onBusinessTypeChange={setSelectedBusinessType}
                        canCreateBranch={canCreateBranch}
                    />
                );
            case BUSINESS_TYPE.FOOD:
                return (
                    <CreateFoodBusinessBranchForm
                        selectedBusinessType={selectedBusinessType}
                        businessTypeOptions={businessTypeOptions}
                        onBusinessTypeChange={setSelectedBusinessType}
                        languageVersionsData={languageVersionsData}
                        canCreateBranch={canCreateBranch}
                    />
                );
            case BUSINESS_TYPE.OTHER:
                return (
                    <CreateOtherBusinessBranchForm
                        selectedBusinessType={selectedBusinessType}
                        businessTypeOptions={businessTypeOptions}
                        onBusinessTypeChange={setSelectedBusinessType}
                        canCreateBranch={canCreateBranch}
                    />
                );
            default:
                return (
                    <CreateOtherBusinessBranchForm
                        selectedBusinessType={selectedBusinessType}
                        businessTypeOptions={businessTypeOptions}
                        onBusinessTypeChange={setSelectedBusinessType}
                        canCreateBranch={canCreateBranch}
                    />
                );
        }
    }, [businessTypeOptions, selectedBusinessType, languageVersionsData, canCreateBranch]);

    if (languageVersionsLoading) return <Spinner />;

    return (
        <Container fluid className="px-0 px-lg-6">
            <InfoBanner positionIcon="top">
                <p className="fs-7 mb-0">
                    {t(
                        "On this page, you can specify information about your branch. Required" +
                            " fields are marked with an asterisk - *."
                    )}
                </p>
                <p className="fs-7 mb-0">
                    {t("The data that customers see in the online record form is marked")}
                    <i className="bi bi-info-circle ms-1"></i>.
                </p>
            </InfoBanner>
            <Row className="d-flex justify-content-between">
                <Col md>
                    <h2 className="mb-7">{t("Branch")}</h2>
                </Col>
                <Col md={3} lg={2} style={{ minWidth: "250px" }}>
                    {isPrimaryLanguage ? (
                        <div
                            className={`position-relative
                             ${selectedBusinessType === BUSINESS_TYPE.FOOD || (selectedBusinessType === BUSINESS_TYPE.OTHER && enabledMenuModule) ? "d-block" : "d-none"}
                             `}
                        >
                            <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                {t("Language version")}
                            </span>
                            <MenuLanguageVersionSelectContainer
                                companyId={Number(currentCompanyId)}
                                isLockedToPrimary
                                fetchPolicy="cache-first"
                            />
                            <div
                                className="position-absolute top-50 end-0 translate-middle-y"
                                style={{ height: "16px" }}
                            >
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={<Tooltip>{t("Translations can be added when editing")}</Tooltip>}
                                >
                                    <div
                                        className=" position-absolute top-50 translate-middle-y end-0 pt-1"
                                        style={{ marginRight: "36px" }}
                                    >
                                        <i className="bi bi-info-circle"></i>
                                    </div>
                                </OverlayTrigger>
                            </div>
                        </div>
                    ) : null}
                </Col>
            </Row>

            {activeBusinessTypeForm}
        </Container>
    );
};

export default CreateBranch;
